import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../stores/store';
import { instructionData } from './instructions/instructionData';
import Menu from './instructions/Menu';
import InstructionDocuments from './instructions/InstructionDocuments';
import { useIsMobile } from '../theme';

export const HelpDialog: React.FC = () => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const isHelpOpen = useStore(useUnityBuildStore).isHelpOpen;
  const setIsHelpOpen = useStore(useUnityBuildStore).setIsHelpOpen;

  const [selectedIndex, setSelectedIndex] = useState(0);

  // Check for target section from localStorage (used by other components to open specific sections)
  useEffect(() => {
    if (isHelpOpen) {
      const targetSection = localStorage.getItem('helpTargetSection');
      if (targetSection) {
        const targetIndex = parseInt(targetSection, 10);
        if (!isNaN(targetIndex) && targetIndex >= 0 && targetIndex < instructionData.length) {
          setSelectedIndex(targetIndex);
          localStorage.removeItem('helpTargetSection'); // Clear after use
        }
      }
    }
  }, [isHelpOpen]);

  const handleClose = () => {
    setIsHelpOpen(false);
  };

  const handleMenuSelect = (index: number) => {
    setSelectedIndex(index);
  };

  return (
    <Dialog
      open={isHelpOpen}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          borderRadius: isMobile ? 0 : 2,
          boxShadow: theme.shadows[4],
          height: isMobile ? '100%' : '80vh',
          maxHeight: isMobile ? '100%' : '80vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.background.paper,
          position: 'sticky',
          top: 0,
          zIndex: 1,
          px: isMobile ? 2 : 3,
          py: isMobile ? 1.5 : 2,
        }}
      >
        <Typography variant="h5" component="h2" color="text.primary">
          Instructions & Help
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': {
              backgroundColor: alpha(theme.palette.action.hover, 0.1),
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          height: '100%',
        }}
      >
        {/* Menu Sidebar */}
        <Menu
          subjects={instructionData}
          selectedIndex={selectedIndex}
          onSelect={handleMenuSelect}
        />

        {/* Content Area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <InstructionDocuments
            subjects={instructionData}
            currentIndex={selectedIndex}
            onNavigate={setSelectedIndex}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default HelpDialog;
